import { inject } from '@angular/core';
import { type Route, Router, type Routes } from '@angular/router';
import { isNil, merge } from 'lodash-es';

import { defaultSubMenuPageGuard } from '@app/auth/guards/default-sub-menu-page.guard';
import { DefaultComponent } from '@app/modules/default.component';
import { ApprovalReviewWorkflowComponent } from '@app/shared/components/approval-review-workflow/approval-review-workflow/approval-review-workflow.component';
import { ChangeLogComponent } from '@app/shared/components/change-log/change-log.component';
import { JobDashboardComponent } from '@app/shared/components/job-dashboard/job-dashboard.component';
import { JobHistoryComponent } from '@app/shared/components/job-history/job-history.component';
import { RunJobDetailsComponent } from '@app/shared/components/run-job/run-job-details/run-job-details.component';
import {
    ACTIONS,
    type MODULES,
    OBJECTS,
    PAGE_INFO,
    PAGE_OBJECT_MAP,
    PAGE_ROUTE_MAP,
    type PAGES,
    SIMULATABLE_OBJECTS,
    WORKFLOWABLE_OBJECTS,
} from '@app/shared/constants/shared.constant';
import type { NgActivatedRouteSnapshot } from '@app/shared/interface/ng-router.interface';
import { BreadcrumbService } from '@app/shared/services/breadcrumb.service';
import { PermissionService } from '@app/shared/services/permission.service';
import { Helpers } from '@app/shared/utils/helpers';
import { TAB_ROUTES } from '@app/shared/utils/route-configs';
import type { ChunkNotFoundDialogService } from '@app/theme/services/chunk-not-found-dialog.service';

/**
 * **************************************************************************************
 * Interfaces for components to work with routes better
 * **************************************************************************************
 */

export interface ModuleRouteData {
    // The module that this route is in
    moduleName: MODULES;
}

export interface RegistryRouteData {
    // The Page that this registry is mapped to (for access control)
    page: PAGES;
    // The Object that this registry enables the user to register
    object: OBJECTS;

    breadcrumb?: any;
}

function validIdGuard(route: NgActivatedRouteSnapshot<null, { id: string }>) {
    /**
     * Checks if the URL has a valid numerical ID or not
     * FIXME: We should think of a better way to handle this ?
     * @param route Route object
     */
    const isValidId = /^\d+$/.test(route.params.id);

    if (!isValidId) {
        inject(Router).navigate(['/not-found']);

        return false;
    }

    return true;
}

function createPageAccessGuard(next: NgActivatedRouteSnapshot<{ object: OBJECTS }>) {
    // If a user does not have access to WRITE any object in the object type, we should not let
    // them open the Create page for that object.

    const permissionService = inject(PermissionService);
    const router = inject(Router);

    // Create page should not be accessible if the user is not inside a project
    if (!permissionService.currentProject()) {
        router.navigate(['unauthorised']);
    }

    if (
        !permissionService.canAccessObject(
            Helpers.getPermissionName(next.data.object, ACTIONS.WRITE),
        )
    ) {
        router.navigate([permissionService.currentProject(), 'unauthorised']);
    }

    return true;
}

/**
 * **************************************************************************************
 * Factory helpers to create angular Routes
 * **************************************************************************************
 */
interface RegistryRoutesOptions {
    // The page for this registry (added to data)
    page: PAGES;
    /**
     * The object specific children components that will be registered to the routes:
     *  - list: The component to list all the objects - At /path
     *  - item: The component to view/edit a single object - At /path/:id
     *  - create: The component to create a single object - At /path/create
     *            If not provided, the item child's values will be used
     *            If the `children` is not provided in the route - by default, take first 2 children of item
     * Other than these, some generic components like JobDashboardComponent, RunJobCompoennt will also
     * be used directly as they are common for all objects
     */
    children: {
        list: Partial<Route>;
        item: Partial<Route>;
        create?: Partial<Route>;
    };
    // The resolver to use for customBreadcrumbs
    resolver?: boolean;
}

interface ModuleRoutesOptions {
    // The module we are creating routes for
    module: MODULES;
    // The list of children to add
    children: Partial<Route>[];
    // Default page to open when the module opens
    defaultPage?: PAGES;
}

export class RouterFactory {
    /**
     * Create various routes within a registry based on configs provided
     * @param options The options to create the registry routes based on
     * @returns The routes for this registry
     */
    static getRegistryRoutes(options: RegistryRoutesOptions): Routes {
        const object = PAGE_OBJECT_MAP[options.page];

        let itemChildren = options.children.item?.children
            ? [...options.children.item.children]
            : [];

        // If default route is not provided - always goes to 'Details' (if present)
        if (itemChildren.every(child => child.path !== '')) {
            itemChildren = [
                {
                    path: '',
                    redirectTo: TAB_ROUTES.DETAILS,
                    pathMatch: 'full',
                },
                ...itemChildren,
            ];
        }

        if (SIMULATABLE_OBJECTS.includes(object) || WORKFLOWABLE_OBJECTS.includes(object)) {
            itemChildren = [
                ...itemChildren,
                {
                    path: TAB_ROUTES.JOBS,
                    component: JobHistoryComponent,
                },
                {
                    path: TAB_ROUTES.DASHBOARD,
                    component: JobDashboardComponent,
                },
            ];
        }

        if (WORKFLOWABLE_OBJECTS.includes(object)) {
            itemChildren = [
                ...itemChildren,
                {
                    path: TAB_ROUTES.APPROVALS,
                    component: ApprovalReviewWorkflowComponent,
                },
            ];
        }

        if (![OBJECTS.USER, OBJECTS.PROJECT_ROLE, OBJECTS.APPROVAL_WORKFLOW].includes(object)) {
            // Add change log to the route here since we have enabled change log
            // tab to all the object (Workflowable and non workflowable)
            itemChildren.push({
                path: TAB_ROUTES.CHANGE_HISTORY,
                component: ChangeLogComponent,
            });
        }

        // By default, we want to show Breadcrumbs (this is only false for ArtifactRepository)
        options.resolver = isNil(options.resolver) ? true : options.resolver;
        let children = [
            merge(
                {
                    path: '',
                },
                options.children.list,
            ),
            merge(
                {
                    path: 'create',
                    canActivate: [createPageAccessGuard],
                    resolve: options.resolver
                        ? { customBreadcrumbs: BreadcrumbService }
                        : undefined,
                },
                // Use `item` info if create not provided
                // But only take the item.children for '' and details - as we only show details tab
                options.children.create
                    ? options.children.create
                    : {
                          ...options.children.item,
                          children: itemChildren.filter(
                              child =>
                                  child.path === '' ||
                                  (child.path as TAB_ROUTES) === TAB_ROUTES.DETAILS,
                          ),
                      },
            ),
            merge(
                {
                    path: ':id',
                    canActivate: [validIdGuard],
                    resolve: options.resolver
                        ? { customBreadcrumbs: BreadcrumbService }
                        : undefined,
                },
                // Always use the children we have created above - it was merged appropriately
                { ...options.children.item, children: itemChildren },
            ),
            merge(
                {
                    path: ':id/preview/:transactionId',
                    canActivate: [validIdGuard],
                    resolve: options.resolver
                        ? { customBreadcrumbs: BreadcrumbService }
                        : undefined,
                    data: { breadcrumb: BreadcrumbService.BREADCRUMB_TITLES.SNAPSHOT_VIEW },
                },
                { ...options.children.item, children: itemChildren },
            ),
        ];

        // If this is a simulatable object - add the routes related to simulations
        // FIXME: Shouldn't this be a child under /:id ?
        if (SIMULATABLE_OBJECTS.includes(object)) {
            children = [
                ...children,
                {
                    path: ':id/run-job',
                    component: RunJobDetailsComponent,
                    canActivate: [validIdGuard],
                    resolve: options.resolver
                        ? { customBreadcrumbs: BreadcrumbService }
                        : undefined,
                    data: { breadcrumb: BreadcrumbService.BREADCRUMB_TITLES.RUN_JOB },
                },
                merge(
                    {
                        path: ':id/simulations/:simulationId',
                        canActivate: [validIdGuard],
                        resolve: options.resolver
                            ? { customBreadcrumbs: BreadcrumbService }
                            : undefined,
                        data: { breadcrumb: BreadcrumbService.BREADCRUMB_TITLES.DASHBOARD },
                    },
                    { component: JobDashboardComponent },
                ),
            ];
        }

        const data: RegistryRouteData = {
            page: options.page,
            object,
            breadcrumb: PAGE_INFO[options.page].name,
        };

        const root = {
            path: PAGE_ROUTE_MAP[options.page],
            component: DefaultComponent,
            data,
            children,
        };

        // NOTE: Keep this as a list for consistency as of now.
        //       May want to clean this up and make it a single route
        return [root];
    }

    static getModuleRoutes(options: ModuleRoutesOptions) {
        let children = options.children ? options.children : [];

        // Add a default route for the module
        // Ref: https://github.com/angular/angular/issues/18605#issuecomment-742105172
        children = [
            {
                path: '',
                data: { defaultPage: options.defaultPage },
                canActivate: [defaultSubMenuPageGuard],
                children: [],
                pathMatch: 'full',
            },
            ...children,
        ];

        // NOTE: We don't need to define the moduleName here because the theme-component
        //       already defines it - because it is needed during the canLoad ModuleAcccessGuard
        const data: ModuleRouteData = { moduleName: options.module };
        const root = {
            path: '',
            component: DefaultComponent,
            data,
            children,
        };

        return [root];
    }

    static handleChunkNotFound<T>(err: Error, dialogService: ChunkNotFoundDialogService): T {
        // FIXME: Currently we reload the application to the older URL. Not the new URL that user is trying to route to.
        //        So, if the user was in HOME going to GENAI-STUDIO, this will redirect back to HOME.
        if (
            err instanceof TypeError &&
            err.message.includes('Failed to fetch dynamically imported module')
        ) {
            // The platform might have been updated as the UI chunk is not found. Show a dialog instead of refreshing.
            dialogService.visible.set(true);
            return;
        }
        throw err;
    }
}
